import { ArrowsClockwise } from "@phosphor-icons/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { DocSyncClient, DocSyncLink } from "~/api/ideTypes";
import {
  useActivity,
  useCollections,
  useCurrentWorkspace,
  useDocSyncLinks,
  useIdeSessionsPage,
  useUpdateDocSyncLink,
} from "~/api/queries";
import { ABILITY } from "~/api/types";
import { BandStrip, Pill, Row, Rows } from "~/features/automation/components";
import { BindingChip, OnlineBadge } from "~/features/console/IdeBadges";
import {
  bindingChipState,
  daysSince,
  isStaleClient,
  orderSessions,
  workspaceBasename,
} from "~/features/console/ideSession";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";
import { Switch } from "~/foundation/components/Switch";
import { Text } from "~/foundation/components/Text";
import { formatRelativeTime } from "~/foundation/utils/time";

/**
 * Markdown folders that keep a collection in step — and, more importantly, the one place a
 * person can find out that they have STOPPED.
 *
 * §E-1 is the reason this surface exists at all. On 2026-08-09 a clone's `.tomato/config.json`
 * lost its `knowItSync` block. The extension is the only sync engine; with no block, no
 * watcher starts, nothing throws, nothing logs — and the server's row stayed perfectly
 * healthy, enabled, correctly pointed, for a MONTH of silent drift. The only observable was a
 * frozen `lastSyncAt`. *The server cannot detect an absent client.*
 *
 * So staleness here is loud, and it is per CLONE, never per link: once a link has several
 * clones a healthy machine masks a stalled one, which is exactly what `clients[]` is for.
 */
function ClientsTable({ clients }: { clients: DocSyncClient[] }) {
  const { t, i18n } = useTranslation();
  return (
    <table className="w-full text-small">
      <thead>
        <tr className="text-text-tertiary">
          <th className="py-1 text-start font-medium">{t("Machine")}</th>
          <th className="py-1 text-start font-medium">{t("Workspace")}</th>
          <th className="py-1 text-start font-medium">{t("Last push")}</th>
          <th className="py-1 text-start font-medium">{t("Last pull")}</th>
        </tr>
      </thead>
      <tbody>
        {clients.map((client) => (
          <tr key={`${client.machineId}-${client.workspacePath}`} className="text-text-secondary">
            <td className="py-1">
              <span className="font-mono">{client.machineId.slice(0, 8)}</span>
              {isStaleClient(client) ? (
                <Pill tone="warning" className="ms-2">
                  {t("Stale")}
                </Pill>
              ) : null}
            </td>
            <td className="py-1">
              <bdi>{workspaceBasename(client.workspacePath)}</bdi>
            </td>
            <td className="py-1">
              {client.lastPushAt
                ? formatRelativeTime(client.lastPushAt, i18n.language)
                : t("Never")}
            </td>
            <td className="py-1">
              {client.lastPullAt
                ? formatRelativeTime(client.lastPullAt, i18n.language)
                : t("Never")}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LinkBlock({ link, canWrite }: { link: DocSyncLink; canWrite: boolean }) {
  const { t, i18n } = useTranslation();
  const { data: collections } = useCollections();
  const update = useUpdateDocSyncLink();

  const collectionName =
    collections?.find((collection) => collection.id === link.collectionId)?.name ??
    link.collectionName ??
    link.collectionId;

  const stalled = link.clients.filter((client) => isStaleClient(client));

  const write = (patch: { enabled?: boolean; pullEnabled?: boolean }, message: string) =>
    update.mutate(
      { link, patch },
      {
        onSuccess: () => toast.success(message),
        onError: () => toast.error(t("Could not update the sync link")),
      },
    );

  return (
    <div className="flex flex-col gap-2 rounded-md border border-divider p-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-body font-medium text-text">{collectionName}</span>
        <span dir="ltr" className="text-start font-mono text-small text-text-tertiary">
          {link.syncPath}
        </span>
      </div>

      {/*
        The whole early-warning system, in one line. "Never synced" is not a neutral absence:
        a link that has never synced is a link whose watcher never started.
      */}
      <p className="text-small text-text-secondary">
        {link.lastSyncAt
          ? t("Last synced {{when}}", {
              when: formatRelativeTime(link.lastSyncAt, i18n.language),
            })
          : t("Never synced")}
        {" · "}
        {t(
          "New: {{new}} · Triaged: {{triaged}} · Conflicts: {{conflict}} · Collaborative: {{collab}}",
          {
            new: link.counts.new,
            triaged: link.counts.triaged,
            conflict: link.counts.conflict,
            collab: link.counts.collab,
          },
        )}
      </p>

      {stalled.length > 0 ? (
        <BandStrip level="warn">
          <p className="text-small text-text">
            {t(
              "{{machine}} has not synced for {{days}} days. Check that `.tomato/config.json` still has its knowItSync block — a lost local config stalls sync with no error anywhere.",
              {
                machine: (stalled[0]?.machineId ?? "").slice(0, 8),
                days: daysSince(stalled[0]?.lastPushAt ?? link.lastSyncAt),
              },
            )}
          </p>
        </BandStrip>
      ) : null}

      <Switch
        checked={link.enabled}
        disabled={!canWrite || update.isPending}
        label={t("Enabled")}
        onCheckedChange={(next) =>
          write({ enabled: next }, next ? t("Sync link enabled") : t("Sync link disabled"))
        }
      />
      <Switch
        checked={link.pullEnabled}
        disabled={!canWrite || update.isPending}
        label={t("Two-way sync (pull)")}
        description={t("Changes made here are written back into the folder on disk.")}
        onCheckedChange={(next) =>
          write(
            { pullEnabled: next },
            next ? t("Two-way sync (pull) enabled") : t("Two-way sync (pull) disabled"),
          )
        }
      />

      {link.clients.length > 0 ? <ClientsTable clients={link.clients} /> : null}
    </div>
  );
}

export function DocumentationSyncPanel() {
  const { t, i18n } = useTranslation();
  const { data: workspace } = useCurrentWorkspace();
  const canWrite = workspace?.abilities[ABILITY.workspaceUpdate] === true;
  const { data: links, isLoading, error } = useDocSyncLinks();
  const { data: sessionsPage } = useIdeSessionsPage();
  const { data: activity } = useActivity({ limit: 100 });

  const sessions = orderSessions(sessionsPage?.sessions ?? []);
  const anyMismatch = sessions.some((session) => bindingChipState(session) === "mismatch");

  /**
   * `events.list` has no prefix filter, so the filtering is client-side — exactly as kms does
   * it. Event NAMES are not translated: `doc_sync.push` is an identifier, and a catalogue
   * entry for it would be a translation of a database value.
   */
  const runs = useMemo(
    () => (activity?.events ?? []).filter((event) => event.name.startsWith("doc_sync.")),
    [activity],
  );

  return (
    <div className="flex flex-col gap-4">
      <Text variant="small">
        {t(
          "Markdown folders linked from the Docit VS Code extension sync into a collection here. Manage each link's status below — files sync on save and on editor activation.",
        )}
      </Text>

      <section className="flex flex-col gap-2">
        <h3 className="text-body font-medium text-text">{t("Connected IDE windows")}</h3>
        {anyMismatch ? (
          <BandStrip level="warn">
            <p className="text-small text-text">
              {t(
                "An IDE window is bound to a different workspace — its sync is refused server-side.",
              )}
            </p>
          </BandStrip>
        ) : null}
        {sessions.length === 0 ? (
          <Text variant="small">{t("No editor windows are connected.")}</Text>
        ) : (
          <Rows>
            {sessions.map((session) => (
              <Row key={session.sessionKey}>
                <span className="min-w-0 flex-1 truncate text-body text-text">
                  {session.workspaceName ?? t("Untitled workspace")}
                </span>
                <OnlineBadge online={session.isOnline} />
                <BindingChip session={session} />
              </Row>
            ))}
          </Rows>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-body font-medium text-text">{t("Links")}</h3>
        {isLoading ? <Spinner label={t("Loading")} /> : null}
        {/*
          A 404 from /doc-sync/* is the EMPTY STATE, never an error: a workspace that has
          never linked a folder is not a workspace with a broken integration.
        */}
        {!isLoading && (error || !links || links.length === 0) ? (
          <Empty
            icon={ArrowsClockwise}
            message={t(
              "No markdown folders are linked yet. Link one from the Docit VS Code extension.",
            )}
          />
        ) : null}
        {(links ?? []).map((link) => (
          <LinkBlock key={link.linkId} link={link} canWrite={canWrite === true} />
        ))}
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-body font-medium text-text">{t("Run history")}</h3>
        {runs.length === 0 ? (
          <Empty message={t("No sync runs recorded yet.")} />
        ) : (
          <Rows>
            {runs.map((event) => (
              <Row key={event.id}>
                {/* An event name is an identifier, not copy — deliberately untranslated. */}
                <span className="min-w-0 flex-1 truncate font-mono text-small text-text">
                  {event.name}
                </span>
                <span className="text-small text-text-secondary">{event.actor?.name ?? "—"}</span>
                <span className="text-small text-text-tertiary">
                  {formatRelativeTime(event.createdAt, i18n.language)}
                </span>
              </Row>
            ))}
          </Rows>
        )}
      </section>
    </div>
  );
}
